import classNames from 'classnames';
import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { isTruthyRailExpanded, useGetRightRailWidth, useRightRail } from 'features/right_rail';
import 'shared/shell_layout/slice';
import { CenterPanel } from './CenterPanel';
import { useShellSidePanelResize } from '../product_refresh/useShellSidePanelResize';
import { DEFAULT_SIDE_PANEL_WIDTH_PX } from './shellBodyResize/constants';
import { ShellBodyResizeProvider, useShellBodyResize } from './shellBodyResize/ShellBodyResizeContext';
import { SidePanel } from './SidePanel';
import { SidePanelResizeHandle } from './SidePanelResizeHandle';
import { useShellSidePanelFullScreenOverlay } from './useShellSidePanelFullScreenOverlay';
import { useSidePanel } from './useSidePanel';
const PANEL_WIDTH = `${DEFAULT_SIDE_PANEL_WIDTH_PX}px`;
const bodyRowCss = "b1halwds";

/** Resize handle supplies horizontal spacing via its own margin when drag is enabled. */
const bodyRowWithResizeHandleCss = "bt8jofm";
const centerPanelCollapsedCss = "c1m4rw3k";
interface ShellBodyCenterPanelProps {
  panelClassName?: string;
  children: ReactNode;
}
const ShellBodyCenterPanel = ({
  panelClassName,
  children
}: ShellBodyCenterPanelProps) => {
  const {
    centerPanelCollapsed
  } = useShellBodyResize();
  return <CenterPanel className={classNames(panelClassName, {
    [centerPanelCollapsedCss]: centerPanelCollapsed
  })}>
      {children}
    </CenterPanel>;
};
interface ShellBodyProps {
  panelClassName?: string;
  children: ReactNode;
  /**
   * When set, overrides `useShellSidePanelResize()` (tests/stories).
   * Does not affect dynamic side panel width from the right rail when drag is off.
   */
  sidePanelResizeEnabled?: boolean;
}

/**
 * Body row: center column, optional resize handle, and side panel.
 * Drag resize is gated by `useShellSidePanelResize()` (`localStorage` `calendly_publisher_side_panel_resize`).
 */
export const ShellBody = ({
  panelClassName,
  children,
  sidePanelResizeEnabled: sidePanelResizeEnabledProp
}: ShellBodyProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const {
    isOpen: isSidePanelOpen
  } = useSidePanel();
  const {
    isOpen: isRightRailOpen,
    current: rightRailCurrent,
    replace: replaceRightRailPane
  } = useRightRail();
  const rightRailWidth = useGetRightRailWidth();
  const railExpanded = isTruthyRailExpanded(rightRailCurrent?.paneProps?.railExpanded);
  const useFullScreenOverlay = useShellSidePanelFullScreenOverlay();
  const sidePanelResizeFlag = useShellSidePanelResize();
  const presetWidthCss = useMemo(() => {
    if (isRightRailOpen && rightRailWidth) {
      return rightRailWidth;
    }
    return PANEL_WIDTH;
  }, [isRightRailOpen, rightRailWidth]);
  const resizeDragEnabled = (sidePanelResizeEnabledProp ?? sidePanelResizeFlag) && !useFullScreenOverlay && isSidePanelOpen;
  const onRailExpandedLayoutExit = useCallback(() => {
    if (rightRailCurrent?.pane !== 'ai_chat') {
      return;
    }
    const paneProps = rightRailCurrent.paneProps ?? {};
    if (!isTruthyRailExpanded(paneProps.railExpanded)) {
      return;
    }
    const {
      railExpanded: _omit,
      ...rest
    } = paneProps;
    replaceRightRailPane('ai_chat', Object.keys(rest).length ? rest : undefined);
  }, [replaceRightRailPane, rightRailCurrent]);
  const bodyRow = <div ref={bodyRef} className={classNames(bodyRowCss, {
    [bodyRowWithResizeHandleCss]: resizeDragEnabled
  })} id="shell-body">
      {resizeDragEnabled ? <ShellBodyCenterPanel panelClassName={panelClassName}>
          {children}
        </ShellBodyCenterPanel> : <CenterPanel className={panelClassName}>
          {children}
        </CenterPanel>}
      {resizeDragEnabled && <SidePanelResizeHandle />}
      <SidePanel desktopClassName={panelClassName} />
    </div>;
  if (!resizeDragEnabled) {
    return bodyRow;
  }
  return <ShellBodyResizeProvider bodyRef={bodyRef} enabled presetWidthCss={presetWidthCss} railExpanded={railExpanded} onRailExpandedLayoutExit={onRailExpandedLayoutExit}>
      {bodyRow}
    </ShellBodyResizeProvider>;
};

require("./ShellBody.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ShellBody.tsx");